import type { ServerDashboardPayload } from "../Contexts/dashboardState";

/**
 * Template file format version. v1 = the original hand-built templates (widgets keyed
 * by `config`, positional `gps-N` metric ids, `{KMn}` title tokens, all subjects
 * full-team). v2 = de-identified packages with a `slots` manifest (see templateTokens).
 */
export function detectTemplateVersion(raw: Record<string, any>): 1 | 2 {
  if (raw?.spec_version === 2 && raw?.slots) return 2;
  return 1;
}

export interface TemplateMetric {
  id: string;   // unique_identifier, e.g. "gps-1"
  name: string; // display name, e.g. "Player Load"
}

export interface TemplateContext {
  gpsMetrics: TemplateMetric[];
  dashboardName: string;
}

/**
 * Converts an exported template JSON into a ServerDashboardPayload ready for importDashboard.
 *
 * Templates reference GPS metrics by positional slot (the first N metrics in the team's GPS
 * metric list, ordered as returned by all_metric_types.gps). The mapping is determined by
 * which metric IDs appear in selectedMetricId fields, in order of first appearance.
 */
export function applyTemplate(
  rawTemplate: Record<string, any>,
  ctx: TemplateContext
): ServerDashboardPayload {
  let json = JSON.stringify(rawTemplate);

  // Collect the GPS metric IDs the template uses, in order of first appearance.
  // This establishes the slot ordering: first seen → ctx.gpsMetrics[0], etc.
  const seen = new Set<string>();
  const templateSlots: string[] = [];
  for (const [, id] of json.matchAll(/"selectedMetricId":"(gps-\d+)"/g)) {
    if (!seen.has(id)) { seen.add(id); templateSlots.push(id); }
  }

  // Replace each template metric slot with the team's actual metric at that position.
  // Quoted JSON string replacement is safe here: '"gps-1"' cannot match '"gps-1-avg"'
  // because the closing quote lands on '-' not '"'.
  templateSlots.forEach((slotId, i) => {
    const teamMetric = ctx.gpsMetrics[i];
    if (!teamMetric) return;
    json = json.replaceAll(`"${slotId}"`, `"${teamMetric.id}"`);
  });

  // Replace title placeholders {KMN_ACWR} before {KMN} to avoid partial match.
  // {KMN} uses 1-based slot index matching templateSlots order.
  templateSlots.forEach((_, i) => {
    const teamMetric = ctx.gpsMetrics[i];
    if (!teamMetric) return;
    const n = String(i + 1);
    json = json.replaceAll(`{KM${n}_ACWR}`, `${teamMetric.name} ACWR`);
    json = json.replaceAll(`{KM${n}}`, teamMetric.name);
  });

  const processed = JSON.parse(json) as Record<string, any>;

  // Template files use "config" as the widget spec key (backend export format).
  // ServerDashboardPayload and importDashboard expect "spec".
  const widgets = (processed.widgets as any[]).map((w: any) => ({
    id: w.id as string,
    type: w.type as string,
    spec: w.config,
    meta: undefined as undefined,
  }));

  return {
    id: processed.id as string,
    name: ctx.dashboardName,
    spec_version: (processed.spec_version as number) ?? 1,
    widgets,
    layout: processed.layout,
  };
}
